import React, { FC, memo, useMemo } from "react";
import { Button, CopyText, Link, Notification } from "@buderus-apps/components";
import Feedback from "../Feedback";
import LoadingIndicator from "../LoadingIndicator";
import { useTranslation } from "react-i18next";
import { TGetSearchStreamData } from "api";
import styles from "./styles.module.scss";
import Teaser from "../Teaser";
import Markdown from "react-markdown";

type Props = TGetSearchStreamData & {
  handleClickSuggestion: (suggestion: string) => void,
  isLoading?: boolean,
  hasError?: boolean,
  errorMessage?: string,
  stars?: number,
  conversationId: string,
  siteId: string
}

type ConvergenceContent = {
  cta: Props['convergenceContent'],
  teaser: Props['convergenceContent']
}

const {
  'chat-response-card': chatResponseCard,
  'chat-response-card__question': chatResponseCardQuestion,
  'chat-response-card__question__text': chatResponseCardQuestionText,
  'chat-response-card__text': chatResponseCardText,
  'chat-response-card__summary': chatResponseCardSummary,
  'chat-response-card__suggestions': chatResponseCardSuggestions,
  'chat-response-card__suggestions__list': chatResponseCardSuggestionsList,
  'chat-response-card__sources': chatResponseCardSources,
  'chat-response-card__sources__list': chatResponseCardSourcesList,
  'chat-response-card__cta-buttons': chatResponseCardCtaButtons,
  'chat-response-card__teasers': chatResponseCardTeasers,
} = styles

const ChatResponseCard: FC<Props> = (props) => {
  const {
    summary,
    suggestions,
    sources,
    question,
    handleClickSuggestion,
    isLoading = false,
    hasError = false,
    errorMessage = '',
    stars,
    conversationId,
    siteId,
    convergenceContent
  } = props

  const { t } = useTranslation()
  const { cta, teaser } = useMemo(() => convergenceContent
    .reduce<ConvergenceContent>((acc, item) => {
      if (item.showTeaser) {
        acc.teaser.push(item)
      } else {
        acc.cta.push(item)
      }
      return acc
    }, { cta: [], teaser: [] }), [convergenceContent, question]
  )

  return question ? <div className={chatResponseCard}>

    <div className={chatResponseCardQuestion}>
      <div className={chatResponseCardQuestionText}>
        <CopyText type='medium'>{question}</CopyText>
      </div>
    </div>

    <LoadingIndicator isLoading={isLoading}/>

    {hasError && (
      <div className={chatResponseCardText}>
        <Notification type={'alert'} text={errorMessage}/>
      </div>
    )}

    {!!summary && (
      <div className={chatResponseCardSummary}>
        <Markdown>{summary}</Markdown>
      </div>
    )}

    {!isLoading && (
      <>
        {cta.length ? <div className={chatResponseCardCtaButtons}>
          {cta.map(btn => {
            return !btn.showTeaser
              ? <Button
                key={btn.link}
                onClick={() => document.location.href = btn.link}
                btnStyle={btn.btnStyle || "cta"}
              >
                {btn.linkText}
              </Button> : ''
          })}
        </div> : ''}

          {suggestions.length ? (
            <div className={chatResponseCardSuggestions}>
              <CopyText type='bold'>{t('FURTHER_SUGGESTIONS')}</CopyText>
              <div className={chatResponseCardSuggestionsList}>
                {suggestions.map((suggestion, key) => (
                  <Button
                    key={suggestion.title + key}
                    btnStyle='link-grey'
                    onClick={() => handleClickSuggestion(suggestion.title)}
                    title={suggestion.title}
                  >
                    {suggestion.title}
                  </Button>
                ))}
              </div>
            </div>
          ) : ''}

          {sources.length ? (
            <div className={chatResponseCardSources}>
              <CopyText type='bold'>{t('SUITABLE_SOURCES')}</CopyText>
              <div className={chatResponseCardSourcesList}>
                {sources.map((source, key) => (
                  <Link key={source.title + key} href={source.href} target='_blank'>{source.title}</Link>
                ))}
              </div>
            </div>
          ) : ''}

          {teaser.length ? <div className={chatResponseCardTeasers}>
            <CopyText type='bold'>{t('TEASER_SUBTITLE')}</CopyText>
            {teaser.map(teaser => {
              return teaser.showTeaser
                ? <Teaser
                  key={teaser.link}
                  title={teaser.title}
                  text={teaser.text}
                  imgSrc={teaser.imageLink}
                  imgAlt={teaser.altText}
                  linkHref={teaser.link}
                  linkText={teaser.linkText}
                />
                : ''
            })}
          </div> : ''}

          {!hasError && (
            <Feedback question={question} stars={stars} conversationId={conversationId} siteId={siteId}/>
          )}
      </>
    )}
  </div> : <></>
}

export default memo(ChatResponseCard)